/**
 * React Query bindings for the phone-number module (`services/phone-number.service.ts`).
 * The hooks only move data and refresh the workspace's numbers; what a screen
 * says about the outcome — a toast, a line in a dialog — is the screen's.
 */

import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type ConnectSipNumberBody,
  type ImportTwilioNumberBody,
  type PhoneNumber,
  type UpdatePhoneNumberBody,
  configureTwilioWebhook,
  connectSipNumber,
  deletePhoneNumber,
  importTwilioNumber,
  listAllPhoneNumbers,
  updatePhoneNumber,
} from "@/services/phone-number.service";

export type { PhoneNumber } from "@/services/phone-number.service";

const refresh = (queryClient: QueryClient, workspaceId: string) =>
  queryClient.invalidateQueries({ queryKey: ["phone-numbers", workspaceId] });

/** Every number of the workspace — what the pickers and the numbers page list. */
export const usePhoneNumbers = (workspaceId?: string) =>
  useQuery<PhoneNumber[]>({
    queryKey: ["phone-numbers", workspaceId],
    queryFn: () => listAllPhoneNumbers(workspaceId!),
    enabled: !!workspaceId,
  });

export const useImportTwilioNumber = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ImportTwilioNumberBody) => importTwilioNumber(body),
    onSuccess: (number) => refresh(queryClient, number.workspace_id),
  });
};

export const useConnectSipNumber = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ConnectSipNumberBody) => connectSipNumber(body),
    onSuccess: (number) => refresh(queryClient, number.workspace_id),
  });
};

export const useUpdatePhoneNumber = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdatePhoneNumberBody }) =>
      updatePhoneNumber(id, updates),
    onSuccess: (number) => refresh(queryClient, number.workspace_id),
  });
};

export const useDeletePhoneNumber = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (number: PhoneNumber) => deletePhoneNumber(number.id),
    onSuccess: (_, number) => refresh(queryClient, number.workspace_id),
  });
};

/** Changes nothing the numbers list shows, so nothing is refreshed. */
export const useConfigureTwilioWebhook = () =>
  useMutation({ mutationFn: (id: string) => configureTwilioWebhook(id) });
